import { Component, Injectable, OnInit, } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, } from '@angular/forms';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { finalize } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { features, routes } from '@app/consts';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { SecurityRoleService } from '@app/modules/security-roles/services/security-role.service';
import { RoleType, SecurityFeature, SecurityRoleSubmit, SecurityRoleVerbose } from '@app/modules/security-setup/models/security-role.model';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { BehaviorSubject, Observable } from 'rxjs';
import { Culture } from '@app/shared/models/system-language/culture.model';
import { TranslateService } from '@ngx-translate/core';
import { CopyRoleDialogComponent } from '../copy-role-dialog/copy-role-dialog.component';
import { modules } from '@app/consts/modules';
import { ModuleConfigurationService } from '@app/core/services/module-configuration/module-configuration.service';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlattener, MatTreeFlatDataSource } from '@angular/material/tree';
import { SelectionModel } from '@angular/cdk/collections';
/**
 * Node for feature item
 */
export class FeatureItemNode {
    name: string;
    children: FeatureItemNode[];
    id: string;
    module?: string; // Optional module for the feature item
    level?: number;
    expandable?: boolean;
}

/** Flat feature item node with expandable and level information */
export class FeatureItemFlatNode {
    name: string;
    id: string;
    module?: string; // Optional module for the flat node
    level: number;
    expandable: boolean;
}

/**
 * Checklist database, it can build a tree structured Json object.
 * Each node in Json object represents a feature item or a category.
 * If a node is a category, it has children items and new items can be added under the category.
 */
@Injectable()
export class ChecklistDatabase {
    dataChange = new BehaviorSubject<FeatureItemNode[]>([]);

    featuresList: FeatureItemNode[] = [
        {
            name: 'Feature-Homepage',
            id: features.HOMEPAGE,
            module: 'allow',
            level: 0,
            expandable: true,
            children: [
                {
                    name: 'Feature-TabHome',
                    id: features.TAB_HOME,
                    module: 'allow',
                    children: [
                        {
                            name: 'Feature-TabWhatsNew',
                            id: features.TAB_WHATS_NEW,
                            children: [],
                            level: 2,
                            expandable: false
                        },
                        {
                            name: 'Feature-TabAnnouncements',
                            id: features.TAB_ANNOUNCEMENTS,
                            children: [],
                            level: 2,
                            expandable: false
                        },
                        {
                            name: 'Feature-TabQuickLinks',
                            id: features.TAB_QUICK_LINKS,
                            children: [],
                            level: 2,
                            expandable: false
                        },
                    ],
                    level: 1,
                    expandable: true
                },
                {
                    name: 'Feature-TabAboutMe',
                    id: features.TAB_ABOUT_ME,
                    module: 'allow',
                    children: [
                        {
                            name: 'Feature-TabPersonalDetails',
                            id: features.TAB_PERSONAL_DETAILS,
                            children: [],
                            level: 2,
                            expandable: false
                        },
                        {
                            name: 'Feature-TabEmergencyContacts',
                            id: features.TAB_EMERGENCY_CONTACTS,
                            children: [],
                            level: 2,
                            expandable: false
                        },
                        {
                            name: 'Feature-TabFamilyDependants',
                            id: features.TAB_FAMILY_DEPENDANTS,
                            children: [],
                            level: 2,
                            expandable: false
                        },
                    ],
                    level: 1,
                    expandable: false
                },
                {
                    name: 'Feature-TabTimeOff',
                    id: features.TAB_TIME_OFF,
                    module: modules.TIME_OFF,
                    children: [
                        {
                            name: 'Feature-TabTimeOffGuides',
                            id: features.TAB_TIME_OFF_GUIDES,
                            children: [],
                            level: 2,
                            expandable: false
                        },
                        {
                            name: 'Feature-TabUserTimeOff',
                            id: features.TAB_USER_TIME_OFF,
                            children: [],
                            level: 2,
                            expandable: false
                        },
                        {
                            name: 'Feature-TabUserBuySell',
                            id: features.TAB_USER_BUY_SELL,
                            children: [],
                            level: 2,
                            expandable: false
                        },
                        {
                            name: 'Feature-TabTOIL',
                            id: features.TAB_TOIL,
                            children: [],
                            module: modules.TOIL,
                            level: 2,
                            expandable: false
                        },
                        {
                            name: 'Feature-TabSchedule',
                            id: features.TAB_SCHEDULE,
                            children: [],
                            level: 2,
                            expandable: false
                        },
                        // {
                        //     name: 'Feature-TabTeamSchedule',
                        //     id: features.TAB_TEAM_SCHEDULE,
                        //     children: [],
                        //     level: 2,
                        //     expandable: false
                        // },
                    ],
                    level: 1,
                    expandable: false
                },
                {
                    name: 'Feature-TabPerformance',
                    id: features.TAB_PERFORMANCE,
                    module: modules.PERFORMANCE,
                    children: [
                        {
                            name: 'Feature-TabGoalGuides',
                            id: features.TAB_GOAL_GUIDES,
                            children: [],
                            level: 2,
                            expandable: false
                        },
                    ],
                    level: 1,
                    expandable: true
                },
            ],
        },
        {
            name: 'Feature-Dashboards',
            id: features.DASHBOARDS,
            level: 0,
            expandable: false,
            children: [],
        },
        {
            name: 'Feature-Talent',
            id: features.TALENT,
            level: 0,
            expandable: false,
            children: [],
        },
        {
            name: 'Feature-Reports',
            id: features.REPORTS,
            level: 0,
            expandable: true,
            children: [
                {
                    name: 'Feature-ReportsAdministrator',
                    id: features.REPORTS_ADMINISTRATOR,
                    children: [],
                    level: 1,
                    expandable: false
                },
                {
                    name: 'Feature-OrganizationChart',
                    id: features.ORG_REPORT,
                    children: [],
                    level: 1,
                    expandable: false
                },
                {
                    name: 'Feature-PositionChart',
                    id: features.POSITIONS_REPORT,
                    children: [],
                    level: 1,
                    expandable: false
                },
                {
                    name: 'Feature-ReportsEmployees',
                    id: features.REPORTS_EMPLOYEES,
                    children: [],
                    level: 1,
                    expandable: false
                },
                {
                    name: 'Feature-ReportsPerformance',
                    id: features.REPORTS_PERFORMANCE,
                    children: [],
                    level: 1,
                    expandable: false
                },
                {
                    name: 'Feature-ReportsTerminations',
                    id: features.REPORTS_TERMINATIONS,
                    children: [],
                    level: 1,
                    expandable: false
                },
                {
                    name: 'Feature-ReportsTOIL',
                    id: features.REPORTS_TOIL,
                    children: [],
                    level: 1,
                    expandable: false
                },
                {
                    name: 'Feature-ReportsTimeOff',
                    id: features.REPORTS_TIME_OFF,
                    children: [],
                    level: 1,
                    expandable: false
                },
                {
                    name: 'Feature-ReportsSecurity',
                    id: features.REPORTS_SECURITY,
                    children: [],
                    level: 1,
                    expandable: false
                },
                {
                    name: 'Feature-ReportsConfiguration',
                    id: features.REPORTS_CONFIGURATION,
                    children: [],
                    level: 1,
                    expandable: false
                },
            ]
        },
        {
            name: 'Feature-TimeOffMenu',
            id: features.TIME_OFF_MENU,
            module: modules.TIME_OFF,
            level: 0,
            expandable: true,
            children: [
                {
                    name: 'Feature-TimeoffAdminStatHolidays',
                    id: features.TIME_OFF,
                    module: modules.TIME_OFF,
                    children: [],
                    level: 1,
                    expandable: false
                },
                {
                    name: 'Feature-TimeoffInLieuAdministration',
                    id: features.TOIL_ADMINISTRATION,
                    module: modules.TOIL,
                    children: [],
                    level: 1,
                    expandable: false
                },
                {
                    id: features.TOIL,
                    module: modules.TOIL,
                    name: 'Feature-TimeOffInLieu',
                    children: [],
                    level: 1,
                    expandable: false
                },
                {
                    id: features.BUY_SELL,
                    name: 'Feature-BuySell',
                    children: [
                        {
                            id: features.BUY_SELL_ADMIN,
                            name: 'Feature-BuySellAdministration',
                            children: [],
                            level: 2,
                            expandable: false
                        },
                        {
                            id: features.BUY_SELL_USER,
                            name: 'Feature-BuySellUser',
                            children: [],
                            level: 2,
                            expandable: false
                        },
                    ],
                    level: 1,
                    expandable: false
                }
            ]
        },
        {
            name: 'Feature-SiteSettings',
            id: 'SiteSettings',
            level: 0,
            expandable: true,
            children: [
                { name: 'Feature-OrganizationAdministration', id: features.ORGANIZATION, children: [], level: 1, expandable: false },
                { name: 'Feature-PositionAdministration', id: features.POSITIONS, children: [], level: 1, expandable: false },
                { name: 'Feature-CostCenters', id: features.COST_CENTERS, children: [], level: 1, expandable: false },
                { name: 'Feature-Lists', id: features.LISTS, children: [], level: 1, expandable: false },
                { name: 'Feature-WorkLocationAdministration', id: features.WORK_LOCATIONS, children: [], level: 1, expandable: false },
                { name: 'Feature-WorkRotationAdministration', id: features.WORK_ROTATIONS, children: [], level: 1, expandable: false },
                { name: 'Feature-Forms', id: features.FORMS, children: [], level: 1, expandable: false },
                { name: 'Feature-SiteConfiguration', id: features.SITE_SETTINGS, children: [], level: 1, expandable: false },
                { name: 'Feature-CurrencyAdministration', id: features.CURRENCY, children: [], level: 1, expandable: false },
                { name: 'Feature-GlobalizationAdministration', id: features.GLOBALIZATION, children: [], level: 1, expandable: false },
                { name: 'Feature-SchedulesAdministration', id: features.SCHEDULES, children: [], level: 1, expandable: false },
                { name: 'Feature-CountryConfig', id: features.COUNTRY_CONFIG, children: [], level: 1, expandable: false },
                { name: 'Feature-BusinessRuleAutomation', id: features.BUSINESS_RULE_AUTOMATION, module: modules.BUSINESS_RULE_AUTOMATION, children: [], level: 1, expandable: false },
                { name: 'Feature-Styling', id: features.SITE_STYLING, children: [], level: 1, expandable: false },
                { name: 'Feature-Integrations', id: features.INTEGRATIONS, children: [], level: 1, expandable: false },
                { name: 'Feature-QuickLinks', id: features.QUICK_LINKS, children: [], level: 1, expandable: false },
                { name: 'Feature-Articles', id: features.ARTICLES, children: [], level: 1, expandable: false },
            ],
        },
        {
            name: 'Feature-SecurityAdministration',
            id: features.SECURITY,
            level: 0,
            expandable: false,
            children: [],
        },
        {
            id: features.PERFORMANCE,
            module: modules.PERFORMANCE,
            name: 'Feature-Performance',
            level: 0,
            expandable: true,
            children: [
                { name: 'Feature-PerformanceGoalPlans', id: features.PERFORMANCE_GOAL_PLANS, children: [], level: 1, expandable: false },
                { name: 'Feature-PerformanceManageGoalPlans', id: features.PERFORMANCE_MANAGE_GOAL_PLANS, children: [], level: 1, expandable: false },
                { name: 'Feature-PerformanceGoalTypes', id: features.PERFORMANCE_GOAL_TYPES, children: [], level: 1, expandable: false },
                { name: 'Feature-PerformanceMyGoals', id: features.PERFORMANCE_MY_GOALS, children: [], level: 1, expandable: false },
                { name: 'Feature-PerformanceDirectReporteeGoals', id: features.PERFORMANCE_DIRECT_REPORTEE_GOALS, children: [], level: 1, expandable: false },
                { name: 'Feature-PerformanceManagerReviewPlans', id: features.PERFORMANCE_MANAGER_REVIEW_PLANS, children: [], level: 1, expandable: false },
                { name: 'Feature-PerformanceReviewPlansAdministration', id: features.PERFORMANCE_REVIEW_PLANS_ADMINISTRATION, children: [], level: 1, expandable: false },
            ]
        },
        {
            id: 'allow',
            name: 'Letters',
            level: 0,
            expandable: true,
            children: [
                {
                    id: features.LETTERS_ADMINISTRATION,
                    name: 'Feature-LettersAdministration',
                    children: [],
                    level: 1,
                    expandable: false
                },
                {
                    id: features.LETTERS_PUBLISHER,
                    name: 'Feature-LettersPublisher',
                    children: [],
                    level: 1,
                    expandable: false
                },
            ]
        },
        {
            id: features.IMPORTS,
            name: 'Feature-Imports',
            level: 0,
            expandable: false,
            children: [],
        },
        {
            id: features.DOCUMENTS_LIBRARY,
            name: 'Feature-Documents',
            module: modules.DOCUMENTS_LIBRARY,
            level: 0,
            expandable: true,
            children: [
                {
                    id: features.DOCUMENTS_LIBRARY_ADMINISTRATION,
                    name: 'Feature-DocumentsLibraryAdministration',
                    children: [],
                    level: 1,
                    expandable: false
                },
                {
                    id: features.DOCUMENTS_ACCESS,
                    name: 'Feature-DocumentsAccess',
                    children: [],
                    level: 1,
                    expandable: false
                },
                {
                    id: features.DOCUMENTS_PUBLISHER,
                    name: 'Feature-DocumentsPublisher',
                    children: [],
                    level: 1,
                    expandable: false
                },
            ]
        }
        // {
        //     name: 'Feature-Benefit Administration',
        //     id: features.BENEFITS,
        // },
    ];

    get data(): FeatureItemNode[] { return this.dataChange.value; }

    constructor(
        private translate: TranslateService,
        private moduleConfigurationService: ModuleConfigurationService,
    ) {
        this.initialize();
    }

    initialize() {
        this.removeFeaturesWithoutModuleAccess();
        // Build the tree nodes from Json object. The result is a list of `FeatureItemNode` with nested
        //     file node as children.
        const data = this.featuresList;

        // Notify the change.
        this.dataChange.next(data);
    }

    // If the client doesnt have access to a module remove it form the list
    removeFeaturesWithoutModuleAccess() {
        this.featuresList = this.featuresList.filter(
            feature => {
                if (feature.children && feature.children.length > 0) {
                    feature.children = feature.children.filter(
                        item => {
                            if (item.module) {
                                return this.moduleConfigurationService.hasModuleAccess(item.module);
                            }
                            else {
                                return true;
                            }
                        }
                    )
                }

                if (feature.module) {
                    return this.moduleConfigurationService.hasModuleAccess(feature.module);
                }
                else {
                    return true;
                }
            }
        )
    }
}

@Component({
    selector: 'app-security-role-editor',
    templateUrl: './security-role-editor.component.html',
    styleUrls: ['./security-role-editor.component.scss'],
    providers: [ChecklistDatabase]
})
export class SecurityRoleEditorComponent implements OnInit {
    public modules: typeof modules = modules;

    public isLoading: boolean;
    public isLoadingUsers: boolean = false;
    public isLoadingOrgs: boolean = false;

    public form: UntypedFormGroup;
    public features: SecurityFeature[] = [];
    public securityRoleId: string;
    public securityRole: SecurityRoleVerbose;
    public securityRoleCopy: SecurityRoleVerbose;
    public cultures: Culture[];

    public gridDataResult: GridDataResult;
    public selectedItems: any[] = [];
    public pageSize: string = '20';
    public skip: number = 0;

    roleTypes: Observable<RoleType[]>;

    // tree items
    /** Map from flat node to nested node. This helps us finding the nested node to be modified */
    flatNodeMap = new Map<FeatureItemFlatNode, FeatureItemNode>();

    /** Map from nested node to flattened node. This helps us to keep the same object for selection */
    nestedNodeMap = new Map<FeatureItemNode, FeatureItemFlatNode>();

    /** A selected parent node to be inserted */
    selectedParent: FeatureItemFlatNode | null = null;

    /** The new item's name */
    newItemName = '';

    treeControl: FlatTreeControl<FeatureItemFlatNode>;

    treeFlattener: MatTreeFlattener<FeatureItemNode, FeatureItemFlatNode>;

    dataSource: MatTreeFlatDataSource<FeatureItemNode, FeatureItemFlatNode>;

    /** The selection for checklist */
    checklistSelection = new SelectionModel<FeatureItemFlatNode>(true /* multiple */);
    public treeFilterForm: UntypedFormGroup = this.fb.group({
        treeFilter: ['']
    });
    formId = 'frm_Vtd6kdbEhNdyI9';
    formData: { id: string; name: string; roleType: string; includeIndirectReports: boolean; description: { culture: string; text: string }[] };
    getFormData: boolean = false;
    formValid: boolean = false;
    formPristine: boolean;

    constructor(
        private dialog: MatDialog,
        private router: Router,
        private route: ActivatedRoute,
        private snackbarService: SnackbarService,
        private fb: UntypedFormBuilder,
        private overlayService: OverlayService,
        private securityRoleService: SecurityRoleService,
        private translate: TranslateService,
        private database: ChecklistDatabase
    ) {
        this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel,
            this.isExpandable, this.getChildren);
        this.treeControl = new FlatTreeControl<FeatureItemFlatNode>(this.getLevel, this.isExpandable);
        this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

        database.dataChange.subscribe(data => {
            this.dataSource.data = data;
        });
    }

    ngOnInit(): void {
        this.roleTypes = this.securityRoleService.getRoleTypes();
        this.securityRoleId = this.getIdInURL();
        this.getSecurityFeatures();

        this.securityRoleId !== null
            ? this.getSecurityRole()
            : this.isLoading = false;
    }

    getLevel = (node: FeatureItemFlatNode) => node.level;

    isExpandable = (node: FeatureItemFlatNode) => node.expandable;

    getChildren = (node: FeatureItemNode): FeatureItemNode[] => node.children;

    hasChild = (_: number, _nodeData: FeatureItemFlatNode) => _nodeData.expandable;

    hasNoContent = (_: number, _nodeData: FeatureItemFlatNode) => _nodeData.name === '';

    /**
     * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
     */
    transformer = (node: FeatureItemNode, level: number) => {
        const existingNode = this.nestedNodeMap.get(node);
        const flatNode = existingNode && existingNode.name === node.name
            ? existingNode
            : new FeatureItemFlatNode();
        flatNode.name = node.name;
        flatNode.id = node.id;
        flatNode.level = level;
        flatNode.expandable = node.children.length > 0;
        this.flatNodeMap.set(flatNode, node);
        this.nestedNodeMap.set(node, flatNode);
        return flatNode;
    }

    /** Whether all the descendants of the node are selected */
    descendantsAllSelected(node: FeatureItemFlatNode): boolean {
        const descendants = this.treeControl.getDescendants(node);
        return descendants.every(child => this.checklistSelection.isSelected(child));
    }

    /** Whether part of the descendants are selected */
    descendantsPartiallySelected(node: FeatureItemFlatNode): boolean {
        const descendants = this.treeControl.getDescendants(node);
        const result = descendants.some(child => this.checklistSelection.isSelected(child));
        return result && !this.descendantsAllSelected(node);
    }

    /** Toggle the feature item selection. Select/deselect all the descendants node */
    featureItemSelectionToggle(node: FeatureItemFlatNode): void {
        this.checklistSelection.toggle(node);
        const descendants = this.treeControl.getDescendants(node);
        this.checklistSelection.isSelected(node)
            ? this.checklistSelection.select(...descendants)
            : this.checklistSelection.deselect(...descendants);
    }

    /**
     * Selects or deselects all parent nodes of the given node.
     * If the node is selected, all its parents will be selected.
     */
    toggleAllParentsSelection(node: FeatureItemFlatNode): void {
        let parent = this.getParentNode(node);
        const isSelected = this.checklistSelection.isSelected(node);

        while (parent) {
            if (isSelected) {
                this.checklistSelection.select(parent);
            }
            parent = this.getParentNode(parent);
        }
    }

    /**
     * Gets the parent node of a flat node.
     */
    getParentNode(node: FeatureItemFlatNode): FeatureItemFlatNode | null {
        const currentLevel = node.level;
        if (currentLevel < 1) {
            return null;
        }
        const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;
        for (let i = startIndex; i >= 0; i--) {
            const currentNode = this.treeControl.dataNodes[i];
            if (currentNode.level === currentLevel - 1) {
                return currentNode;
            }
        }
        return null;
    }

    getSelectedTreeNodes(): FeatureItemFlatNode[] {
        return this.checklistSelection.selected;
    }

    filterTree() {
        const filterValue = this.treeFilterForm.get('treeFilter')?.value?.toLowerCase() || '';
        if (filterValue) {
            this.dataSource.data = this.database.data.filter(node => {
                return node.name.toLowerCase().includes(filterValue) ||
                    (node.children && node.children.some(child => child.name.toLowerCase().includes(filterValue)));
            });
        } else {
            this.dataSource.data = this.database.data;
        }
        this.treeControl.expandAll();
    }

    getIdInURL(): string {
        let IdInURL: string;

        this.route.paramMap.subscribe(
            params => IdInURL = params.get('roleId')
        );

        return IdInURL;
    }

    getSecurityRole() {
        this.isLoading = true;

        this.securityRoleService.getSecurityRole(this.securityRoleId)
            .subscribe(
                res => {
                    this.securityRole = res;
                    this.isLoading = false;
                    this.createForm();
                }
            );
    }

    getSecurityFeatures() {
        this.securityRoleService.getFeatures()
            .subscribe(
                res => {
                    this.features = res;

                    // update feature names in the tree from the fetched features
                    this.database.featuresList.forEach(item => {
                        const matchingFeature = this.features.find(f => f.id === item.id);
                        if (matchingFeature) {
                            item.name = matchingFeature.name;
                        }

                        if (item.children && item.children.length > 0) {
                            item.children.forEach(child => {
                                const matchingChildFeature = this.features.find(f => f.id === child.id);
                                if (matchingChildFeature) {
                                    child.name = matchingChildFeature.name;
                                }

                                if (child.children && child.children.length > 0) {
                                    child.children.forEach(grandchild => {
                                        const matchingGrandchildFeature = this.features.find(f => f.id === grandchild.id);
                                        if (matchingGrandchildFeature) {
                                            grandchild.name = matchingGrandchildFeature.name;
                                        }
                                    });
                                }
                            });
                        }
                    });
                    this.database.initialize();

                    this.createForm();
                }
            );
    }

    createForm() {
        this.formData = {
            id: this.securityRole ? this.securityRole.id : null,
            name: this.securityRole ? this.securityRole.name : '',
            roleType: this.securityRole ? this.securityRole.roleType?.id : null,
            includeIndirectReports: this.securityRole ? this.securityRole.includeIndirectReports : false,
            description: this.securityRole ? this.securityRole.description : []
        }

        // check the existing selected features in the check list
        if (this.securityRole && this.securityRole.features) {
            this.treeControl.dataNodes.forEach(node => {
                if (this.securityRole.features.some(feature => feature.id === node.id)) {
                    this.checklistSelection.select(node);
                }
            });
        }
        this.treeControl.collapseAll();
    }

    formDataEmitted(formData) {
        this.save(formData);
    }

    formPristineEmitted(formPristine: boolean) {
        this.formPristine = formPristine;
    }

    formStatusUpdated(formValid) {
        this.formValid = formValid;
    }

    save(formData) {
        this.overlayService.show();

        let securityRoleSubmit: SecurityRoleSubmit = {
            name: formData.name,
            features: this.getSelectedTreeNodes().map(elem => elem.id).filter(elem => elem !== '').filter(elem => elem !== "allow"),
            roleType: formData.roleType,
            includeIndirectReports: formData.includeIndirectReports,
            description: formData.description
        };
        if (this.securityRole) {
            this.updateSecurityRole(this.securityRole.id, securityRoleSubmit);
        } else {
            this.createSecurityRole(securityRoleSubmit);
        }
    }

    createSecurityRole(securityRoleSubmit: SecurityRoleSubmit) {
        this.securityRoleService.postSecurityRole(securityRoleSubmit)
            .pipe(
                finalize(() => this.overlayService.hide())
            )
            .subscribe(
                (res) => {
                    this.navigateToSecurityRoles();
                    this.snackbarService.openSnackBar('Security Role saved successfully', 'clear', 'success');
                }
            );
    }

    updateSecurityRole(securityRoleId: string, securityRoleSubmit: SecurityRoleSubmit) {
        this.securityRoleService.putSecurityRole(securityRoleId, securityRoleSubmit)
            .pipe(
                finalize(() => this.overlayService.hide())
            )
            .subscribe(
                (res) => {
                    this.snackbarService.openSnackBar('Security Role saved successfully', 'clear', 'success');
                },
                err => {
                    this.snackbarService.openSnackBar(err, 'clear', 'warn');
                }
            );
    }

    navigateToSecurityRoles() {
        this.router.navigate([`${routes.SECURITY}/${routes.ROLES}`]);
    }

    copySecurityRole() {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;
        dialogConfig.data = {
            roleId: this.securityRoleId
        };

        const dialogRef = this.dialog.open(CopyRoleDialogComponent, dialogConfig);
        dialogRef.afterClosed().subscribe(
            data => {
                if (data) {
                    this.router.navigate([`${routes.SECURITY}/${routes.ROLES}/${routes.EDITOR}/${data}`]);
                }
            }
        );
    }

    deleteSecurityRole() {
        const dialogConfig = new MatDialogConfig();

        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;

        dialogConfig.data = {
            text: `Are you sure you want to delete this Security Role?`
        };

        const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
        dialogRef.afterClosed().subscribe(
            data => {
                if (data === true) {
                    this.overlayService.show();

                    this.securityRoleService.deleteSecurityRole(this.securityRoleId)
                        .pipe(finalize(() => this.overlayService.hide()))
                        .subscribe(
                            (res) => {
                                this.snackbarService.openSnackBar(this.translate.instant('DeletedSuccessfully'), 'clear', 'success');
                                this.navigateToSecurityRoles();
                            }
                        );
                }
            }
        );
    }
}
